/**
 * Canonical hourly pricing by CareHub role.
 * Source: NEMA_Care Attendant & Nurse Hourly Service List Draft (Delhi NCR).
 * Keep in sync with nemacareBE/config/nemaHourlyServiceList.js.
 *
 * Nurse rows store Delhi NCR competitive bands; defaultPriceInr is the catalog
 * booking default (within the band) used when a caregiver has not set a rate.
 */

/** Shown beside procedure ranges in UI — not a guaranteed NemaCare price. */
export const DELHI_NCR_PROCEDURE_PRICING_DISCLAIMER =
  "Indicative Delhi NCR market reference range — not a guaranteed NemaCare price or automatic charge.";

/** Care Attendant — sheet "Care Attendant Hourly Package" */
export const CARE_ATTENDANT_HOURLY_PACKAGES = [
  {
    id: "hygiene_personal",
    servicePack: "Service Pack 1",
    title: "Hygiene Care & Personal Assistance",
    durationLabel: "1.5 - 2 hrs",
    durationMinutes: 120,
    tasks: [
      "Oral Care",
      "Sponge Bath / Normal Bath",
      "Hair Wash",
      "Toileting Assistance",
      "Diaper Change",
      "Urinal / Bed Pan Assistance",
      "Feeding",
    ],
    pricingMode: "single",
    defaultPriceInr: 500,
  },
  {
    id: "companion",
    servicePack: "Service Pack 2",
    title: "Companion Service",
    durationLabel: "1 - 6 hrs",
    tasks: ["Hospital Visit"],
    pricingMode: "tiers",
    tiers: [
      { id: "t2", label: "1 hr – 2 hrs", durationMinutes: 120, defaultPriceInr: 500 },
      { id: "t4", label: "1 hr – 4 hrs", durationMinutes: 240, defaultPriceInr: 750 },
      { id: "t6", label: "1 hr – 6 hrs", durationMinutes: 360, defaultPriceInr: 900 },
    ],
  },
];

/** Nurse — sheet "Nurse Procedures" */
export const NURSE_HOURLY_PROCEDURES = [
  { id: "injection", label: "Injection Administration (IM/IV/SC)", durationMinutes: 30, rangeMinInr: 400, rangeMaxInr: 800, defaultPriceInr: 550 },
  { id: "iv_drip", label: "IV Drip Administration (for 2 hours)", durationMinutes: 120, rangeMinInr: 700, rangeMaxInr: 1500, defaultPriceInr: 1000 },
  { id: "cannulation", label: "Cannulation (IV Line)", durationMinutes: 30, rangeMinInr: 400, rangeMaxInr: 800, defaultPriceInr: 550 },
  {
    id: "iv_therapy",
    label: "IV Infusion Monitoring",
    durationMinutes: 60,
    rangeMinInr: 300,
    rangeMaxInr: 700,
    defaultPriceInr: 450,
    unitLabel: "per hour",
  },
  { id: "dressing_simple", label: "Wound Dressing (Simple)", durationMinutes: 30, rangeMinInr: 500, rangeMaxInr: 1000, defaultPriceInr: 650 },
  { id: "dressing_complex", label: "Wound Dressing (Complex)", durationMinutes: 60, rangeMinInr: 1000, rangeMaxInr: 2000, defaultPriceInr: 1400 },
  { id: "catheter_insert", label: "Urinary Catheter Insertion", durationMinutes: 30, rangeMinInr: 900, rangeMaxInr: 2000, defaultPriceInr: 1200 },
  { id: "catheter_remove", label: "Catheter Removal", durationMinutes: 20, rangeMinInr: 500, rangeMaxInr: 800, defaultPriceInr: 600 },
  { id: "catheter_care", label: "Catheter Care / Flush", durationMinutes: 20, rangeMinInr: 400, rangeMaxInr: 700, defaultPriceInr: 500 },
  { id: "ryles_tube_insert", label: "Ryles (NG) Tube Insertion", durationMinutes: 30, rangeMinInr: 900, rangeMaxInr: 2000, defaultPriceInr: 1200 },
  { id: "ryles_tube_feed", label: "Ryles Tube Feeding Visit", durationMinutes: 30, rangeMinInr: 500, rangeMaxInr: 800, defaultPriceInr: 600 },
  { id: "urine_bag", label: "Urine Bag Change", durationMinutes: 15, rangeMinInr: 300, rangeMaxInr: 600, defaultPriceInr: 400 },
  { id: "enema", label: "Enema Administration", durationMinutes: 30, rangeMinInr: 500, rangeMaxInr: 900, defaultPriceInr: 650 },
  { id: "bp_monitoring", label: "Blood Pressure Monitoring", durationMinutes: 20, rangeMinInr: 300, rangeMaxInr: 500, defaultPriceInr: 350 },
  { id: "sugar_monitoring", label: "Blood Sugar Monitoring", durationMinutes: 20, rangeMinInr: 300, rangeMaxInr: 500, defaultPriceInr: 350 },
  { id: "ecg", label: "ECG at Home", durationMinutes: 30, rangeMinInr: 700, rangeMaxInr: 1500, defaultPriceInr: 1000 },
  { id: "nebulization", label: "Nebulization", durationMinutes: 20, rangeMinInr: 400, rangeMaxInr: 700, defaultPriceInr: 500 },
  { id: "oxygen", label: "Oxygen Administration / Setup", durationMinutes: 30, rangeMinInr: 600, rangeMaxInr: 1200, defaultPriceInr: 800 },
  { id: "suctioning", label: "Suctioning", durationMinutes: 30, rangeMinInr: 700, rangeMaxInr: 1500, defaultPriceInr: 1000 },
  { id: "tracheostomy", label: "Tracheostomy Care", durationMinutes: 45, rangeMinInr: 1000, rangeMaxInr: 2500, defaultPriceInr: 1600 },
  { id: "stoma", label: "Stoma Care", durationMinutes: 30, rangeMinInr: 700, rangeMaxInr: 1500, defaultPriceInr: 1000 },
  { id: "colostomy", label: "Colostomy Care", durationMinutes: 30, rangeMinInr: 800, rangeMaxInr: 1500, defaultPriceInr: 1100 },
  { id: "peg_tube", label: "PEG Tube Care", durationMinutes: 30, rangeMinInr: 800, rangeMaxInr: 1500, defaultPriceInr: 1100 },
  { id: "medication", label: "Medication Administration", durationMinutes: 20, rangeMinInr: 400, rangeMaxInr: 800, defaultPriceInr: 550 },
  { id: "vitals", label: "Vital Signs Monitoring", durationMinutes: 20, rangeMinInr: 300, rangeMaxInr: 600, defaultPriceInr: 400 },
  {
    id: "companion_hospital",
    label: "Companion Nursing Hospital Visit (for 2 - 6 Hrs)",
    durationMinutes: 240,
    rangeMinInr: 1000,
    rangeMaxInr: 2500,
    defaultPriceInr: 1500,
  },
];

/** Physiotherapist — session catalogue (minimum session duration + average rate). */
export const PHYSIO_THERAPY_PROCEDURES = [
  { id: "short_assessment", label: "Short Assessment & Routine Rehab", durationMinutes: 30, rangeMinInr: 600, rangeMaxInr: 600, defaultPriceInr: 600 },
  { id: "cardiopulmonary", label: "Cardiopulmonary Physiotherapy", durationMinutes: 45, rangeMinInr: 900, rangeMaxInr: 900, defaultPriceInr: 900 },
  { id: "neurological", label: "Neurological Physiotherapy", durationMinutes: 60, rangeMinInr: 1000, rangeMaxInr: 1000, defaultPriceInr: 1000 },
  { id: "orthopedic", label: "Orthopedic Physiotherapy", durationMinutes: 45, rangeMinInr: 800, rangeMaxInr: 800, defaultPriceInr: 800 },
  { id: "elderly_care", label: "Elderly Care Physiotherapy", durationMinutes: 45, rangeMinInr: 700, rangeMaxInr: 700, defaultPriceInr: 700 },
  { id: "stroke_paralysis", label: "Stroke & Paralysis Rehabilitation", durationMinutes: 60, rangeMinInr: 1100, rangeMaxInr: 1100, defaultPriceInr: 1100 },
  { id: "post_surgery", label: "Post Surgery Rehabilitation", durationMinutes: 45, rangeMinInr: 850, rangeMaxInr: 850, defaultPriceInr: 850 },
  { id: "sports", label: "Sports Physiotherapy", durationMinutes: 45, rangeMinInr: 900, rangeMaxInr: 900, defaultPriceInr: 900 },
  { id: "pediatric", label: "Pediatric Physiotherapy", durationMinutes: 45, rangeMinInr: 800, rangeMaxInr: 800, defaultPriceInr: 800 },
  { id: "womens", label: "Women's Physiotherapy", durationMinutes: 45, rangeMinInr: 900, rangeMaxInr: 900, defaultPriceInr: 900 },
];
